import { useLayoutEffect, useMemo, useState,useEffect } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import axios from 'axios';
import { getToken as getAuthToken } from 'api/auth';
import {apiUrl} from 'api/http';
// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
// remove these hardcoded imports
// import menuItem from 'menu-items';
// import { MenuFromAPI } from 'menu-items/dashboard';
// import menuItemsConfig from 'menu-items';

//  use the prefix-aware menu builder
import { buildMenu } from 'menu-items';

import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from 'config';
import { useGetMenu, useGetMenuMaster } from 'api/menu';

function isFound(arr, str) {
  return arr.items.some((element) => element.id === str);
}

// strip the app basename (e.g., "/zingoblast") from a pathname so we can detect "/admin"
function stripBase(pathname, base) {
  const b = String(base || '/').replace(/\/+$/, '');
  if (!b || b === '/') return pathname;
  return pathname.startsWith(b) ? pathname.slice(b.length) || '/' : pathname;
}

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { menuLoading } = useGetMenu();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const [selectedID, setSelectedID] = useState('');
  const [selectedItems, setSelectedItems] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  // ------- NEW: compute prefix (/admin or /) from current URL safely with basename -------
  const basename = import.meta.env.VITE_APP_BASE_NAME || '/';
  const effectivePath = stripBase(window.location.pathname, basename);
  const isAdmin = /^\/admin(\/|$)/.test(effectivePath);
  const prefix = isAdmin ? '/admin' : '/';
  // Build the menu ONCE per prefix change
  const menuItemsConfig = useMemo(
    () => buildMenu(prefix, { admin: isAdmin }),
    [prefix, isAdmin]
  );

  // If you still want to respect menuLoading (from API), keep it as a dep — but avoid loops
  useLayoutEffect(() => {
    // If you ever re-enable API menus, you can merge here.
    setMenuItems({ items: [...menuItemsConfig.items] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuLoading, menuItemsConfig]);
  useEffect(() => {
    const token = getAuthToken({ admin: false });
    if (!token) return;

    (async () => {
      try {
        const { data } = await axios.get(apiUrl('facebook/pages/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const pages = Array.isArray(data) ? data : data?.results || [];
        try { sessionStorage.setItem('fb_pages_cache', JSON.stringify(pages)); } catch {}
        const menuConfig = buildMenu(prefix, { admin: isAdmin, pages });
        setMenuItems(menuConfig);
      } catch (err) {
        console.error('[Navigation] failed to load pages', err);
        setMenuItems({ items: [] });
      }
    })();
  }, [prefix, isAdmin]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  // Horizontal compaction
  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && { url: item.url })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List
              key={item.id}
              sx={{
                zIndex: 0,
                ...(isHorizontal && { mt: 0.5 }),
                // ensure Dividers inside this List don’t add unexpected top margins
                '& .MuiDivider-root': { mt: 0 }
              }}
            >
              {!isHorizontal && index !== 0 && <Divider sx={{ mb: 0.5 }} />}
              <NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedID={selectedID}
            selectedItems={selectedItems}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 0) : 0,
        ...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
}
